import { useState, useEffect } from 'react'
import { AlertTriangle } from 'lucide-react'
import { useConfirm } from '@papan/core-ui/ui'
import { useFileIO } from '@papan/files/client/file-io'

export type ConflictView = 'mine' | 'saved'

interface ConflictBannerProps {
  fileId: string
  /** Keep the saved version — resets room from disk, dropping unsaved edits */
  onDiscard: () => void
  /** Keep my changes — force-saves Y.Doc to disk */
  onOverwrite: () => void
  /** Current comparison view */
  view: ConflictView
  /** Switch comparison view */
  onViewChange: (view: ConflictView) => void
  /** Whether the saved version can be shown beside yours. False where the viewer can't read
   *  version history (a share-link visitor) — the toggle is left out, only Keep my changes. */
  canCompare?: boolean
}

/**
 * Inline banner shown when a file conflict is detected
 * (disk changed externally while editor has unsaved changes).
 *
 * The toggle picks which version you are looking at and the single action
 * keeps exactly that one — its label follows the toggle, so what you see is
 * what you keep. Keeping the saved version asks first: the unsaved edits were
 * never written, so unlike an overwrite (the replaced file stays in version
 * history) it cannot be undone, and it resets the doc for every co-editor.
 */
export function ConflictBanner({ fileId: _fileId, onDiscard, onOverwrite, view, onViewChange, canCompare = true }: ConflictBannerProps) {
  const confirm = useConfirm()
  const keepSaved = canCompare && view === 'saved'

  const handleKeep = async () => {
    if (!keepSaved) {
      onOverwrite()
      return
    }
    const confirmed = await confirm({
      title: 'Keep the saved version?',
      message: "Your unsaved changes will be discarded, including for anyone else editing this file. This can't be undone.",
      type: 'danger',
      confirmText: 'Keep saved version',
    })
    if (confirmed) onDiscard()
  }

  return (
    <div className="px-3 py-1.5 bg-orange-500/15 border-b border-orange-500/30 flex items-center gap-2 shrink-0">
      <AlertTriangle size={14} className="text-orange-400 shrink-0" />
      <span className="text-xs text-orange-300">File changed while you were editing</span>

      {canCompare && <div className="flex items-center gap-0.5 ml-2 bg-black/20 rounded p-0.5">
        <button
          onClick={() => onViewChange('mine')}
          className={`px-2 py-0.5 text-[11px] rounded transition-colors ${
            view === 'mine'
              ? 'bg-orange-500/30 text-orange-200'
              : 'text-orange-400/60 hover:text-orange-300'
          }`}
        >
          Your changes
        </button>
        <button
          onClick={() => onViewChange('saved')}
          className={`px-2 py-0.5 text-[11px] rounded transition-colors ${
            view === 'saved'
              ? 'bg-orange-500/30 text-orange-200'
              : 'text-orange-400/60 hover:text-orange-300'
          }`}
        >
          Saved version
        </button>
      </div>}

      <div className="flex-1" />

      <button
        onClick={() => { void handleKeep() }}
        title={keepSaved
          ? 'Discard your unsaved changes and keep the saved file'
          : 'Save your version over the file — the replaced version stays in version history'}
        className="px-2 py-0.5 text-[11px] rounded bg-orange-600 hover:bg-orange-500 text-white transition-colors"
      >
        {keepSaved ? 'Keep saved version' : 'Keep my changes'}
      </button>
    </div>
  )
}

/**
 * Hook to load the saved (disk) version of a file for conflict comparison.
 * Returns the content as a string, loading state, and a refetch function.
 *
 * Read through the editor's FileIO (#930 / I3), never `filesApi` directly: in the drive that is the same
 * `filesApi.getFileContent`, but in the share view the drive route answers 401 to a share session — the pane opened
 * EMPTY and the visitor compared against nothing. The share FileIO reads the saved bytes through the session's own
 * serve URL.
 */
export function useSavedVersion(fileId: string, active: boolean, conflictId?: number) {
  const fileIO = useFileIO()
  const [content, setContent] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  // Reset cache when fileId changes or a new conflict starts (conflictId increments)
  useEffect(() => {
    setContent(null)
  }, [fileId, conflictId])

  useEffect(() => {
    if (!active || content !== null) return
    let cancelled = false
    setLoading(true)
    fileIO.loadContent(fileId)
      .then(blob => blob.text())
      .then(text => { if (!cancelled) setContent(text) })
      .catch(() => { if (!cancelled) setContent(null) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [active, content, fileId, fileIO])

  return { content, loading }
}
